import React, { useState, useEffect, useRef } from 'react'
import { useEffectOnce } from 'usehooks-ts'
import styled from 'styled-components'

// helpers
const capitalize = (word: any) => {
  return word.charAt(0).toUpperCase() + word.slice(1)
}
const commatize = (word: any) => {
  const replaced = word.toString().replace('.', ',')
  return replaced.replace(',0', '')
}

const BarChart = ({
  tenant,
  colorMode,
  isMobile,
  blau,
  rot,
  gelb,
  percBlau,
  percRot,
  percGelb,
  countBlau,
  countRot,
  countGelb,
  totalRegions,
}: {
  tenant?: object
  colorMode?: string
  isMobile?: boolean
  blau?: string
  rot?: string
  gelb?: string
  percBlau: number
  percRot: number
  percGelb: number
  countBlau: number
  countRot: number
  countGelb: number
  totalRegions: number
}) => {
  const barsRef = React.createRef<HTMLDivElement>()
  const [barsWidth, setBarsWidth] = useState(barsRef.current?.offsetWidth)
  const [blauWidth, setBlauWidth] = useState(0)
  const [rotWidth, setRotWidth] = useState(0)
  const [beigeWidth, setBeigeWidth] = useState(0)
  const [gelbWidth, setGelbWidth] = useState(0)
  const labelGelbRef = React.createRef<HTMLDivElement>()
  const [labelGelbWidth, setLabelGelbWidth] = useState(
    labelGelbRef.current?.offsetWidth
  )
  const percBeige = (100 - (percBlau + percRot + percGelb)).toFixed(1)

  useEffect(() => {
    setBarsWidth(barsRef.current?.offsetWidth)
    setBlauWidth(barsWidth ? (barsWidth * percBlau) / 100 : 0)
    setRotWidth(barsWidth ? (barsWidth * percRot) / 100 : 0)
    setGelbWidth(barsWidth ? (barsWidth * percGelb) / 100 : 0)
    setBeigeWidth(barsWidth ? barsWidth - blauWidth - rotWidth - gelbWidth : 0)
    setLabelGelbWidth(labelGelbRef.current?.offsetWidth)
    /*
    console.log('labelGelbWidth:', labelGelbWidth)
    console.log('gelbWidth:', gelbWidth)
    console.log('beigeWidth:', beigeWidth)
    console.log('rotWidth:', rotWidth)
    */
    //console.log('percGelb:', percGelb)
  }, [
    barsRef,
    barsWidth,
    percBlau,
    percRot,
    percGelb,
    blauWidth,
    rotWidth,
    gelbWidth,
    beigeWidth,
    labelGelbRef,
    labelGelbWidth,
  ])

  return (
    <Barchart className={percBlau + percRot ? 'active' : 'inactive'}>
      <div className='labels'>
        {percBlau > 0 && (
          <div className='blau label' style={{ left: 0 }}>
            {capitalize(blau)}
            <span>{commatize(percBlau.toFixed(1)) + '%'}</span>
          </div>
        )}
        {percGelb > 0 && (
          <div
            ref={labelGelbRef}
            className='gelb label'
            style={{
              left:
                labelGelbWidth &&
                labelGelbWidth < gelbWidth + beigeWidth + rotWidth
                  ? percBlau + '%'
                  : 'unset',
              right:
                labelGelbWidth &&
                labelGelbWidth > gelbWidth + beigeWidth + rotWidth
                  ? percRot + '%'
                  : 'unset',
            }}
          >
            {labelGelbWidth &&
              labelGelbWidth > gelbWidth + beigeWidth + rotWidth && (
                <span>{commatize(percGelb.toFixed(1)) + '%'}</span>
              )}
            {capitalize(gelb)}
            {labelGelbWidth &&
              labelGelbWidth <= gelbWidth + beigeWidth + rotWidth && (
                <span>{commatize(percGelb.toFixed(1)) + '%'}</span>
              )}
          </div>
        )}
        {percRot > 0 && (
          <div className='rot label' style={{ right: 0 }}>
            <span>{commatize(percRot.toFixed(1)) + '%'}</span>
            {capitalize(rot)}
          </div>
        )}
      </div>
      <div ref={barsRef} className='bars'>
        <div className='blau bar' style={{ width: percBlau + '%' }}>
          <div className='bg' />
          <div className='filler' />
        </div>
        <div className='gelb bar' style={{ width: percGelb + '%' }}>
          <div className='bg' />
          <div className='filler' />
        </div>
        <div className='beige bar' style={{ width: percBeige + '%' }}>
          <div className='bg' />
          <div className='filler' />
        </div>
        <div className='rot bar' style={{ width: percRot + '%' }}>
          <div className='bg' />
          <div className='filler' />
        </div>
      </div>
    </Barchart>
  )
}
const Barchart = styled.div`
  max-width: 900px;
  //laptops
  @media (max-height: 1400px) and (orientation: landscape) {
    max-width: 90vh;
  }
  //mobile
  @media (max-width: 1024px) {
    max-width: 87%;
  }

  @media (min-width: 600px) {
    padding: 0 5rem;
  }

  min-height: 24px;
  margin: 0 auto 3rem;
  .labels {
    position: relative;
    bottom: 30px;
    @media (max-width: 600px) {
      bottom: 21px;
    }
    .label {
      font-size: 1.15em;
      @media (max-width: 600px) {
        font-size: 0.925em;
      }
      font-weight: 600;
      padding-left: 4px;
      position: absolute;
      white-space: nowrap;
      /*
      -webkit-transition: left 0.2s;
      -moz-transition: left 0.2s;
      -o-transition: left 0.2s;
      transition: left 0.2s;
      */
      span {
        font-size: 0.8em;
        font-weight: 400;
      }
    }
    .rot {
      color: var(--red-main);
      padding-right: 4px;
      color: whte;
      span {
        margin-right: 0.5rem;
      }
    }
    .blau {
      color: var(--blue-main);
      span {
        margin-left: 0.5rem;
      }
    }
    .gelb {
      color: #e9c412; // darker yellow-main
      padding-left: 0;
      top: 55px;
      @media (max-width: 600px) {
        top: 44px;
      }
      span {
        margin-right: 0.5rem;
        margin-left: 0.5rem;
      }
    }
  }
  .bars {
    height: 20px;
    @media (max-width: 600px) {
      height: 14px;
    }
    width: 100%;
    .bar {
      height: 20px;
      @media (max-width: 600px) {
        height: 14px;
      }
      position: relative;
      display: inline-block;
      /*
      -webkit-transition: width 0.2s;
      -moz-transition: width 0.2s;
      -o-transition: width 0.2s;
      transition: width 0.2s;
      */

      .bg {
        width: 100%;
        height: 100%;
        background-color: var(--beige-bright-3);
        position: absolute;
        top: 0;
      }
    }
    .filler {
      opacity: 0.7;
      width: 100%;
      height: 100%;
      position: absolute;
      top: 0;
    }
    .rot .filler {
      background-color: var(--red-main);
    }
    .blau .filler {
      background-color: var(--blue-main);
    }
    .gelb .filler {
      background-color: var(--yellow-main);
    }
    .beige .filler {
      background-color: var(--beige-dark-2);
    }
  }
  &.inactive {
    opacity: 0;
  }
`

export default BarChart
